import { useState, useMemo } from "react";
import { v4 as uuidV4 } from "uuid";
import moment from "moment-timezone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { CreateDestinationTemplateValidation, useCreateDestinationTemplate, DocumentVariant, AcceptedDestinationPacketFields, SignerType, useListDestinationPacketTemplates, type DestinationPacketDocument } from "@/lib/api/destinationPackets";
import { safeParseDecimal } from "@/lib/api/common";
import { PdfField } from "../types";
import { UploadPresignedFile } from "@/lib/file";
import { CommonDestinationTimezone } from "@/lib/utils";

interface CreateTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fields: PdfField[];
  originalFile: File;
  onSuccess: () => void;
}

type PacketMode = "existing" | "new";

interface TemplateFormData {
  document_variant: DocumentVariant;
  version_label: string;
  version_reason: string;
  name: string;
  packet_mode: PacketMode;
  selected_packet_id: string;
  packet_name: string;
}

function generateDefaultFormData(): TemplateFormData {
  return {
    document_variant: DocumentVariant.DESTINATION_INFO,
    version_label: "v1",
    version_reason: "v1",
    name: "Destination Packet",
    packet_mode: "new",
    selected_packet_id: "",
    packet_name: "",
  }
}

function genFieldDestType(field_type: string, field_id: string) {
  if (field_type !== 'signature') {
    return undefined;
  }

  if (/date/i.test(field_id || '')) {
    return 'date';
  }

  if (/attachment/i.test(field_id || '')) {
    return 'attachment';
  }

  if (/initials/i.test(field_id || '')) {
    return 'signature_initials';
  }

  return 'signature';
}

export function CreateTemplateDialog({ open, onOpenChange, fields, originalFile, onSuccess }: CreateTemplateDialogProps) {
  const { toast } = useToast();
  const createDestinationTemplate = useCreateDestinationTemplate();
  const listTemplatesResult = useListDestinationPacketTemplates();
  const [formData, setFormData] = useState<TemplateFormData>(generateDefaultFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get unique packets that are up to 5 days old and don't already have the selected variant
  const availablePackets = useMemo(() => {
    const templates = listTemplatesResult?.data?.data?.data || [];
    const now = moment.tz(CommonDestinationTimezone);
    const fiveDaysAgo = now.clone().subtract(5, 'days');

    // Group templates by packet ID and track variants
    const packetMap = new Map<string, { 
      id: string; 
      name: string | null; 
      oldestDate: moment.Moment;
      variants: Set<DocumentVariant>;
    }>();

    templates.forEach((template: DestinationPacketDocument) => {
      if (!template.destination_template_packet_id || !template.DestinationTemplatePacket) {
        return;
      }

      const packetId = template.destination_template_packet_id;
      const packetName = template.DestinationTemplatePacket.name;
      const relevantDate = moment.tz(template.DestinationTemplatePacket.created_at, CommonDestinationTimezone);
      
      if (!packetMap.has(packetId)) {
        packetMap.set(packetId, {
          id: packetId,
          name: packetName,
          oldestDate: relevantDate,
          variants: new Set([template.document_variant]),
        });
      }
    });

    // Filter to only packets created within the last 5 days and that don't already have the selected variant
    return Array.from(packetMap.values())
      .filter(packet => {
        const isWithin5Days = packet.oldestDate.isAfter(fiveDaysAgo);
        const doesNotHaveVariant = !packet.variants.has(formData.document_variant);
        return isWithin5Days && doesNotHaveVariant;
      })
      .sort((a, b) => b.oldestDate.valueOf() - a.oldestDate.valueOf());
  }, [listTemplatesResult, formData.document_variant]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!formData.document_variant || !formData.version_label) {
      toast({
        title: "Error",
        description: "Document variant and version label are required",
        variant: "destructive",
      });
      return;
    }

    // Validate packet selection
    if (formData.packet_mode === "existing" && !formData.selected_packet_id) {
      toast({
        title: "Error",
        description: "Please select a packet to add to",
        variant: "destructive",
      });
      return;
    }

    if (formData.packet_mode === "new" && !formData.packet_name) {
      toast({
        title: "Error",
        description: "Please enter a packet name",
        variant: "destructive",
      });
      return;
    }

    // Validate required tracked fields are present
    const fieldIds = fields.map(f => f.field_id);
    const requiredFields = [
      { id: AcceptedDestinationPacketFields.CUSTOMER_NAME, name: "Customer Name" },
      { id: AcceptedDestinationPacketFields.CUSTOMER_EMAIL, name: "Customer Email" },
    ];
    if (formData.document_variant === DocumentVariant.CREDIT_APPLICATION) {
          requiredFields.push(...([
              { id: AcceptedDestinationPacketFields.LENDER_NAME, name: "Lender Name" },
              { id: AcceptedDestinationPacketFields.LENDER_EMAIL, name: "Lender Email" },
          ]));
      }

    const missingFields = requiredFields.filter(req => !fieldIds.includes(req.id));
    if (missingFields.length > 0) {
      const missingNames = missingFields.map(f => f.name).join(", ");
      toast({
        title: "Error",
        description: `Missing required fields: ${missingNames}`,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Tracked lender fields that must be assigned to PRIMARY_CUSTOMER
      const trackedLenderFields = [
        AcceptedDestinationPacketFields.LENDER_NAME,
        AcceptedDestinationPacketFields.LENDER_EMAIL,
      ];

      const processedFields = fields.map(field => {
        // Safety pass: ensure tracked lender fields have PRIMARY_CUSTOMER as signer
        const isTrackedLenderField = trackedLenderFields.includes(field.field_id as AcceptedDestinationPacketFields);
        const signerId = isTrackedLenderField 
          ? (field.signer_id || SignerType.LENDER)
          : (field.signer_id || undefined);

        return {
          field_type: field.field_type,
          field_id: field.field_id,
          field_key: field.field_key,
          field_dest_type: field.field_dest_type || genFieldDestType(field.field_type, field.field_id),
          position_data: {
            x: safeParseDecimal({})(field.position_data.x).toString(),
            y: safeParseDecimal({})(field.position_data.y).toString(),
            width: safeParseDecimal({})(field.position_data.width).toString(),
            height: safeParseDecimal({})(field.position_data.height).toString(),
            text_size: safeParseDecimal({})(field.position_data.text_size).toNumber() || 6,
            page: safeParseDecimal({})(field.position_data.page).toNumber(),
          },
          freeform_value: field.freeform_value,
          signer_id: signerId,
          is_required: field.is_required ?? false,
          is_readonly: field.is_readonly ?? false,
        };
      });

      const parseResult = CreateDestinationTemplateValidation.safeParse({
        template_content_type: "destination_packet",
        parent_group_id: uuidV4(),
        document_variant: formData.document_variant,
        destination_template_packet_id: formData.packet_mode === "existing" ? formData.selected_packet_id : undefined,
        packet_name: formData.packet_mode === "new" ? formData.packet_name : undefined,
        template: {
          name: formData.name ? formData.name : undefined,
          version_label: formData.version_label,
          version_reason: formData.version_reason,
          require_all_signers: true,
          send_auto_reminders: false,
          use_signer_order: true,
          fields: processedFields,
        }
      });

      if (!parseResult.success) {
        console.error("failed to parse api body", parseResult.error);
        toast({
          title: "Error",
          description: "Invalid data",
          variant: "destructive",
        });
        return;
      }

      const apiResult = await createDestinationTemplate(parseResult.data)
      if (!apiResult.success) {
        throw apiResult;
      }

      const uploadResult = await UploadPresignedFile(apiResult.data.upload_url, originalFile)
      if (!uploadResult.success) {
        toast({
          title: "Error",
          description: "Failed to upload file",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Created Template",
        description: formData.packet_mode === "existing" 
          ? "Template added to existing packet" 
          : "Template created with new packet",
      });

      onSuccess();
      handleOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create template",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setFormData(generateDefaultFormData());
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="md:min-w-[40vw]">
        <DialogHeader>
          <DialogTitle>Create Destination Packet Template</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="document_variant">Document Variant</Label>
              <Select
                value={formData.document_variant}
                onValueChange={(value: DocumentVariant) =>
                  setFormData(prev => ({ 
                    ...prev, 
                    document_variant: value,
                    // Clear selected packet when variant changes since available packets will change
                    selected_packet_id: prev.packet_mode === "existing" ? "" : prev.selected_packet_id,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select document variant..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DocumentVariant.DESTINATION_INFO}>Destination Info</SelectItem>
                  <SelectItem value={DocumentVariant.CREDIT_APPLICATION}>Credit Application</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="packet_mode">Packet Selection</Label>
              <Select
                value={formData.packet_mode}
                onValueChange={(value: PacketMode) =>
                  setFormData(prev => ({ 
                    ...prev, 
                    packet_mode: value,
                    selected_packet_id: value === "existing" ? prev.selected_packet_id : "",
                    packet_name: value === "new" ? prev.packet_name : "",
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select packet mode..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="existing" disabled={availablePackets.length === 0}>Add to existing packet</SelectItem>
                  <SelectItem value="new">Create new packet</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.packet_mode === "existing" && (
              <div className="grid gap-2">
                <Label htmlFor="selected_packet_id">Select Packet</Label>
                <Select
                  value={formData.selected_packet_id}
                  onValueChange={(value) =>
                    setFormData(prev => ({ ...prev, selected_packet_id: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a packet..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availablePackets.length === 0 ? (
                      <SelectItem value="empty" disabled>
                        No packets available (must be created within last 5 days and not already have this variant)
                      </SelectItem>
                    ) : (
                      availablePackets.map((packet) => (
                        <SelectItem key={packet.id} value={packet.id}>
                          {packet.name || `Packet ${packet.id.slice(0, 8)}`}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.packet_mode === "new" && (
              <div className="grid gap-2">
                <Label htmlFor="packet_name">Packet Name</Label>
                <Input
                  id="packet_name"
                  value={formData.packet_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, packet_name: e.target.value }))}
                  placeholder="Enter packet name"
                  required
                />
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Destination Packet"
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="version_label">Version Label</Label>
              <Input
                id="version_label"
                value={formData.version_label}
                onChange={(e) => setFormData(prev => ({ ...prev, version_label: e.target.value }))}
                placeholder="v1"
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="version_reason">Version Reason (Optional)</Label>
              <Textarea
                id="version_reason"
                value={formData.version_reason}
                onChange={(e) => setFormData(prev => ({ ...prev, version_reason: e.target.value }))}
                placeholder="Enter reason for this version..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Template"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

